// === Shared table primitives — consistent design across all admin pages ===
// Header uses theme.palette.background.default, which is always a distinct shade
// from theme.palette.background.paper in every theme (dark or light).
// Usage:
//   <Table>
//     <AppTableHead>
//       <TableRow>
//         <AppTableHeadCell>Column</AppTableHeadCell>
//       </TableRow>
//     </AppTableHead>
//     <TableBody>
//       <AppTableRow>...</AppTableRow>
//     </TableBody>
//   </Table>

import { styled, alpha, darken } from '@mui/material/styles';
import { TableHead, TableCell, TableRow } from '@mui/material';
import type { TableHeadProps, TableCellProps, TableRowProps } from '@mui/material';

// Header: darken background.paper for dark themes (creates a clearly distinct
// band above the body rows), or a primary tint for light themes.
export const AppTableHead = styled(TableHead)<TableHeadProps>(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'light'
      ? alpha(theme.palette.primary.main, 0.09)
      : darken(theme.palette.background.paper, 0.35),
  '& .MuiTableRow-root': { backgroundColor: 'transparent' },
}));

export const AppTableHeadCell = styled(TableCell)<TableCellProps>(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: '0.8125rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '14px 16px',
  whiteSpace: 'nowrap',
  opacity: 0.85,
}));

// Body rows — taller height, consistent padding, zebra striping
export const AppTableRow = styled(TableRow)<TableRowProps>(({ theme }) => ({
  height: 60,
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.mode === 'light'
      ? alpha(theme.palette.primary.main, 0.04)
      : alpha(theme.palette.common.white, 0.03),
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiTableCell-root': {
    padding: '12px 16px',
    fontSize: '0.875rem',
  },
}));

// Default rows-per-page for all paginated tables
export const DEFAULT_ROWS_PER_PAGE = 15;
export const ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50, 100, 250];
